import React, { createContext, useContext, useState, useEffect, useCallback, ReactNode } from 'react';
import { authApi } from '../services/authApi';
import { persistSessionUser, readSessionUser } from '../sessionStorage';
import { SessionUser, LoginRequest } from '../types';
import { SESSION_EXPIRED_EVENT } from '../../common/CommonTypes';
import { SESSION_ACTIVITY_CHECK_MS, SESSION_TIMEOUT_MS } from '../sessionConfig';

interface AuthContextType {
  user: SessionUser | null;
  loading: boolean;
  error: string | null;
  login: (credentials: LoginRequest) => Promise<boolean>;
  logout: () => Promise<void>;
  isAuthenticated: boolean;
}

const AuthContext = createContext<AuthContextType | undefined>(undefined);

export const AuthProvider: React.FC<{ children: ReactNode }> = ({ children }) => {
  const [user, setUser] = useState<SessionUser | null>(null);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const isLoggingOutRef = React.useRef(false);

  useEffect(() => {
    setUser(readSessionUser());
    setLoading(false);
  }, []);

  useEffect(() => {
    const handleSessionExpired = () => {
      isLoggingOutRef.current = false;
      persistSessionUser(null);
      setUser(null);
      setError(null);
      setLoading(false);
      void authApi.logout().catch(() => {
        // Best-effort server session destruction; local state already cleared
      });
    };

    window.addEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
    return () => window.removeEventListener(SESSION_EXPIRED_EVENT, handleSessionExpired);
  }, []);

  useEffect(() => {
    if (!user) {
      return;
    }

    const lastActivityRef = { current: Date.now() };
    const recordActivity = () => {
      lastActivityRef.current = Date.now();
    };

    const activityEvents: Array<keyof WindowEventMap> = [
      'mousedown',
      'keydown',
      'scroll',
      'touchstart',
    ];
    activityEvents.forEach(eventName => {
      window.addEventListener(eventName, recordActivity, { passive: true });
    });

    const intervalId = window.setInterval(() => {
      const idleMs = Date.now() - lastActivityRef.current;
      if (idleMs >= SESSION_TIMEOUT_MS) {
        window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
      }
    }, SESSION_ACTIVITY_CHECK_MS);

    const originalFetch = window.fetch.bind(window);
    window.fetch = async (input: RequestInfo | URL, init?: RequestInit) => {
      const response = await originalFetch(input, init);
      if (response.status === 403) {
        const url = typeof input === 'string'
          ? input
          : input instanceof Request
            ? input.url
            : String(input);
        if (!url.includes('/auth/login')) {
          window.dispatchEvent(new CustomEvent(SESSION_EXPIRED_EVENT));
        }
      }
      return response;
    };

    return () => {
      window.clearInterval(intervalId);
      activityEvents.forEach(eventName => {
        window.removeEventListener(eventName, recordActivity);
      });
      window.fetch = originalFetch;
    };
  }, [user]);

  const login = useCallback(async (credentials: LoginRequest): Promise<boolean> => {
    try {
      setLoading(true);
      setError(null);
      const response = await authApi.login(credentials);
      persistSessionUser(response.user);
      setUser(response.user);
      return true;
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Login failed');
      return false;
    } finally {
      setLoading(false);
    }
  }, []);

  const logout = useCallback(async (): Promise<void> => {
    try {
      isLoggingOutRef.current = true;
      persistSessionUser(null);
      setUser(null);
      setError(null);
      setLoading(false);
      await authApi.logout();
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Logout failed');
      setLoading(false);
    } finally {
      setTimeout(() => {
        isLoggingOutRef.current = false;
      }, 500);
    }
  }, []);

  const value = {
    user,
    loading,
    error,
    login,
    logout,
    isAuthenticated: !!user,
  };

  return <AuthContext.Provider value={value}>{children}</AuthContext.Provider>;
};

export const useAuthContext = () => {
  const context = useContext(AuthContext);
  if (context === undefined) {
    throw new Error('useAuthContext must be used within an AuthProvider');
  }
  return context;
};
